import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LANDING } from "@/constants/testIds";
import useReveal from "@/hooks/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Original hero portrait (~13MB PNG). Proxied through wsrv.nl for mobile-friendly
// delivery — auto-WebP, resized to 900px wide, served gzipped. Drops payload from
// ~13MB to ~150–250KB without visible quality loss.
const HEADSHOT_RAW =
    "customer-assets.emergentagent.com/job_173b05cc-3444-4f30-abad-393e993a69a0/artifacts/60vu194v_Naomi%20Lennon%20Headshot%20real.png";
const HEADSHOT = `https://wsrv.nl/?url=${HEADSHOT_RAW}&w=1000&q=82&output=webp`;

const IMG_DIRECTOR =
    "https://images.pexels.com/photos/8088387/pexels-photo-8088387.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";
const IMG_WORKSPACE =
    "https://images.pexels.com/photos/32560539/pexels-photo-32560539.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";
const IMG_PAPER =
    "https://images.pexels.com/photos/7952505/pexels-photo-7952505.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";
const IMG_CURRICULUM =
    "https://images.pexels.com/photos/17580689/pexels-photo-17580689.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";
const IMG_CREW =
    "https://images.unsplash.com/photo-1577190651915-bf62d54d5b36?crop=entropy&cs=srgb&fm=jpg&h=1100&w=1400&q=85";

// ============ Reusable bits ============

const Overline = ({ children, className = "" }) => (
    <p
        className={`font-mono text-[10px] md:text-xs uppercase tracking-[0.3em] text-ink-muted ${className}`}
    >
        {children}
    </p>
);

const SectionLabel = ({ n, children }) => (
    <div className="flex items-baseline gap-4 mb-10 md:mb-16">
        <span className="font-mono text-xs text-ink-muted tracking-widest">
            § {n.toString().padStart(2, "0")}
        </span>
        <span className="h-px flex-1 bg-ink-border" />
        <Overline>{children}</Overline>
    </div>
);

const PrimaryButton = ({ children, onClick, type = "button", testId, className = "" }) => (
    <button
        type={type}
        onClick={onClick}
        data-testid={testId}
        className={`inline-flex items-center justify-center bg-ink text-ivory font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 hover:bg-rust transition-colors duration-300 ${className}`}
    >
        {children}
    </button>
);

const SecondaryButton = ({ children, onClick, testId, className = "" }) => (
    <button
        type="button"
        onClick={onClick}
        data-testid={testId}
        className={`inline-flex items-center justify-center bg-transparent border border-ink text-ink font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 hover:bg-ink hover:text-ivory transition-colors duration-300 ${className}`}
    >
        {children}
    </button>
);

// ============ Nav ============

function Nav({ onJoin, onWorkWithMe }) {
    return (
        <header className="fixed top-0 left-0 right-0 z-50 bg-ivory/85 backdrop-blur-sm border-b border-ink-border">
            <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
                <a
                    href="#top"
                    data-testid={LANDING.navLogo}
                    className="font-display text-xl tracking-tight"
                >
                    Naomi Lennon
                </a>
                <nav className="hidden lg:flex items-center gap-8 font-mono text-[11px] uppercase tracking-[0.2em] text-ink-muted">
                    <a href="#why-naomi" data-testid={LANDING.navAbout} className="hover:text-ink transition-colors">
                        About
                    </a>
                    <a href="#advisory" data-testid={LANDING.navAdvisory} className="hover:text-ink transition-colors">
                        Advisory
                    </a>
                    <a href="#advisory" data-testid={LANDING.navInfluencer} className="hover:text-ink transition-colors">
                        Influencer Growth
                    </a>
                    <a href="#curriculum" data-testid={LANDING.navMasterclass} className="hover:text-ink transition-colors">
                        Masterclass
                    </a>
                    <a href="#pricing" data-testid={LANDING.navPricing} className="hover:text-ink transition-colors">
                        Pricing
                    </a>
                </nav>
                <button
                    onClick={onWorkWithMe}
                    data-testid={LANDING.navWorkWithMe}
                    className="font-mono text-[11px] uppercase tracking-[0.2em] bg-ink text-ivory px-5 py-2.5 hover:bg-rust transition-colors"
                >
                    Work with me
                </button>
            </div>
        </header>
    );
}

// ============ Hero ============

function Hero({ scrollToPricing, scrollToAdvisory }) {
    return (
        <section id="top" className="relative pt-28 md:pt-32 pb-20 md:pb-28">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                    {/* LEFT: copy */}
                    <div className="lg:col-span-7 lg:pt-6">
                        <p className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.22em] text-ink-muted leading-relaxed max-w-xl mb-10">
                            In 2010 I bet my career on YouTube creators. Most people
                            thought I was wrong.
                        </p>

                        <h1 className="font-display font-light leading-[1.05] tracking-tight text-ink text-3xl sm:text-4xl md:text-5xl lg:text-[56px] xl:text-[64px]">
                            I built one of the first{" "}
                            <em className="italic text-rust">creator management companies</em>{" "}
                            in the world.
                            <br />
                            <span className="block mt-3">
                                For 15 years I helped creators turn audiences into
                                businesses.
                            </span>
                            <span className="block mt-3 text-ink-muted">
                                In 2019, the company I built was{" "}
                                <em className="italic text-ink">acquired.</em>
                            </span>
                            <span className="block mt-3">
                                Now I’m teaching others how to{" "}
                                <em className="italic">build theirs.</em>
                            </span>
                        </h1>

                        <p className="mt-10 font-display italic text-2xl md:text-3xl text-ink">
                            Sign slower. Understand deeper. Build bigger.
                        </p>

                        <div className="mt-10 max-w-xl space-y-4 font-sans text-base md:text-lg leading-relaxed text-ink-muted">
                            <p className="text-ink">I work with two types of people.</p>
                            <p>
                                <span className="text-ink font-medium">
                                    Companies and founders
                                </span>{" "}
                                building creator-driven growth — advisory, influencer
                                strategy, and creator programme design.
                            </p>
                            <p>
                                <span className="text-ink font-medium">Individuals</span>{" "}
                                who want to launch their own creator management company —
                                from anywhere in the world.
                            </p>
                        </div>

                        <div className="mt-10 flex flex-col sm:flex-row gap-4">
                            <PrimaryButton
                                onClick={scrollToPricing}
                                testId={LANDING.heroJoinBtn}
                            >
                                Join the masterclass →
                            </PrimaryButton>
                            <SecondaryButton
                                onClick={scrollToAdvisory}
                                testId={LANDING.heroWorkWithMeBtn}
                            >
                                Work with me →
                            </SecondaryButton>
                        </div>
                    </div>

                    {/* RIGHT: headshot */}
                    <div className="lg:col-span-5">
                        <div className="relative">
                            <div className="absolute -top-4 -left-4 right-8 bottom-8 border border-ink-border" />
                            <img
                                src={HEADSHOT}
                                alt="Naomi Lennon"
                                data-testid={LANDING.heroHeadshot}
                                width="1000"
                                height="1000"
                                loading="eager"
                                fetchpriority="high"
                                decoding="async"
                                className="relative w-full h-auto object-cover grayscale-[12%] contrast-[1.02]"
                            />
                            <div className="mt-6 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
                                <span>Naomi Lennon</span>
                                <span>Operator · 2026</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Credibility strip */}
                <div className="mt-16 md:mt-20 border-t border-b border-ink-border overflow-hidden">
                    <div className="flex marquee whitespace-nowrap py-5">
                        {[1, 2].map((dup) => (
                            <div key={dup} className="flex items-center gap-12 px-6 font-mono text-[11px] uppercase tracking-[0.25em] text-ink-muted">
                                <span>15+ Years</span>
                                <span>·</span>
                                <span>6,000+ Campaigns</span>
                                <span>·</span>
                                <span>Agency Acquired 2019</span>
                                <span>·</span>
                                <span>YouTube</span>
                                <span>·</span>
                                <span>TikTok</span>
                                <span>·</span>
                                <span>Instagram</span>
                                <span>·</span>
                                <span>Snapchat</span>
                                <span>·</span>
                                <span>Vine</span>
                                <span>·</span>
                                <span>VidCon</span>
                                <span>·</span>
                                <span>Cannes Lions</span>
                                <span>·</span>
                                <span>OMR Germany</span>
                                <span>·</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

// ============ Current Work ============

function CurrentWork() {
    return (
        <section className="bg-ink text-ivory py-28 md:py-36 reveal">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="flex items-baseline gap-4 mb-10 md:mb-16">
                    <span className="font-mono text-xs text-ivory/50 tracking-widest">
                        § 01
                    </span>
                    <span className="h-px flex-1 bg-ivory/20" />
                    <p className="font-mono text-[10px] md:text-xs uppercase tracking-[0.3em] text-ivory/70">
                        Current Work
                    </p>
                </div>

                <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-5xl">
                    I’m not teaching from the sidelines.{" "}
                    <em className="italic text-rust">I’m still in the room.</em>
                </h2>

                <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                    <div className="lg:col-span-7 space-y-5 font-sans text-base md:text-lg leading-relaxed text-ivory/75 max-w-2xl">
                        <p>
                            Today I advise AI companies, publishers, creator platforms,
                            and brands building inside the creator economy.
                        </p>
                        <p>
                            Current engagements include influencer growth, creator
                            acquisition, partnership strategy, and creator-led
                            go-to-market programmes.
                        </p>
                        <p className="text-ivory">
                            Alongside the masterclass, I work with a small number of
                            retained clients on the strategic and operational challenges
                            of building creator-driven businesses at scale.
                        </p>
                        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ivory/50 pt-2">
                            Clients undisclosed.
                        </p>
                    </div>

                    <aside className="lg:col-span-5">
                        <div className="border border-ivory/20 p-8">
                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ivory/60 mb-4">
                                Enquiries
                            </p>
                            <a
                                href="mailto:naomi@naomilennon.com"
                                data-testid={LANDING.currentWorkEmailLink}
                                className="font-display text-2xl md:text-3xl text-ivory hover:text-rust transition-colors break-all"
                            >
                                naomi@naomilennon.com
                            </a>
                        </div>
                    </aside>
                </div>
            </div>
        </section>
    );
}

// ============ Two Paths ============

function TwoPaths({ scrollToAdvisory, scrollToPricing }) {
    return (
        <section className="py-28 md:py-40 reveal">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <SectionLabel n={2}>How I Can Help</SectionLabel>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-ink-border border border-ink-border">
                    {/* LEFT — Companies */}
                    <article className="bg-ivory p-8 md:p-12 flex flex-col">
                        <Overline className="mb-6">For Companies & Founders</Overline>
                        <h3 className="font-display text-3xl md:text-4xl text-ink leading-tight mb-6">
                            You’re building in the creator economy and need the kind of
                            thinking that only comes from{" "}
                            <em className="italic">15 years of operating inside it.</em>
                        </h3>
                        <div className="space-y-4 font-sans text-base text-ink-muted leading-relaxed flex-1">
                            <p>
                                I advise AI companies, publishers, creator platforms, and
                                global brands on creator strategy, influencer growth,
                                talent ecosystems, and go-to-market planning.
                            </p>
                            <p>
                                I also build and scale influencer marketing programmes
                                for active clients across AI, SaaS, and creator
                                technology.
                            </p>
                        </div>
                        <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-rust">
                            → Advisory · Influencer Growth · Strategy Sprints · Keynotes
                        </p>
                        <PrimaryButton
                            onClick={scrollToAdvisory}
                            testId={LANDING.twoPathsCompaniesBtn}
                            className="mt-8 self-start"
                        >
                            Work with me →
                        </PrimaryButton>
                    </article>

                    {/* RIGHT — Individuals */}
                    <article className="bg-ivory-deep p-8 md:p-12 flex flex-col">
                        <Overline className="mb-6 !text-rust">For Individuals</Overline>
                        <h3 className="font-display text-3xl md:text-4xl text-ink leading-tight mb-6">
                            You want to build a creator management company — or you’re
                            already managing creators and want to do it{" "}
                            <em className="italic">properly.</em>
                        </h3>
                        <div className="space-y-4 font-sans text-base text-ink-muted leading-relaxed flex-1">
                            <p>
                                The Creator Management Masterclass teaches you how to
                                find creators, sign talent, negotiate deals, build
                                recurring revenue, and create a business with real
                                long-term value.
                            </p>
                            <p className="text-ink">
                                No agency experience needed. Build from anywhere in the
                                world.
                            </p>
                        </div>
                        <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-rust">
                            → 9 modules · Founding: $497 · Standard: $997
                        </p>
                        <SecondaryButton
                            onClick={scrollToPricing}
                            testId={LANDING.twoPathsIndividualsBtn}
                            className="mt-8 self-start"
                        >
                            Join the masterclass →
                        </SecondaryButton>
                    </article>
                </div>
            </div>
        </section>
    );
}

// ============ Cameron Dallas Quote ============

function CameronQuote() {
    return (
        <section className="bg-ink text-ivory py-28 md:py-40">
            <div className="max-w-5xl mx-auto px-6 md:px-12 text-center reveal">
                <blockquote className="font-display italic font-light text-4xl sm:text-5xl md:text-6xl leading-[1.1] tracking-tight">
                    “The Anna Wintour of
                    <br />
                    creator management.”
                </blockquote>
                <p className="mt-12 font-mono text-xs uppercase tracking-[0.3em] text-ivory/70">
                    — Cameron Dallas, creator · 22M+ followers
                </p>
            </div>
        </section>
    );
}

// ============ The Problem ============

function TheProblem() {
    return (
        <section className="py-28 md:py-40 reveal">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <SectionLabel n={3}>The Problem</SectionLabel>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                    <div className="lg:col-span-6">
                        <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                            The creator economy has
                            <br />
                            exploded. <em className="italic">The standards haven’t.</em>
                        </h2>
                    </div>
                    <div className="lg:col-span-6 font-sans text-base md:text-lg leading-relaxed text-ink-muted space-y-5">
                        <p>
                            Right now, thousands of people are calling themselves creator
                            managers. Most of them aren’t doing it badly because they’re
                            lazy. They’re doing it badly because nobody taught them how to
                            do it properly.
                        </p>
                        <p>
                            They’re signing anyone with a following. Using AI to blast
                            outreach at scale. Chasing follower counts instead of building
                            real businesses.
                        </p>
                        <p className="text-ink">
                            The creators deserve better. And the managers who actually
                            understand this business — who know how to evaluate talent,
                            structure deals, and build something with long-term value — are
                            quietly running the most respected operations in the space.
                        </p>
                        <p>
                            For fifteen years there was no course that taught this
                            properly. I’m changing that.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}

// ============ Why Now ============

// ============ The Opportunity ============

function TheOpportunity() {
    return (
        <section className="py-28 md:py-40 bg-ivory-deep reveal">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <SectionLabel n={4}>The Opportunity</SectionLabel>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
                    <div className="lg:col-span-7">
                        <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                            Own a piece of the creator economy{" "}
                            <em className="italic">without being the creator.</em>
                        </h2>
                        <div className="mt-10 space-y-5 font-sans text-base md:text-lg leading-relaxed text-ink-muted max-w-xl">
                            <p>
                                Not everyone wants to be on camera. But someone still has to
                                build the businesses behind the people who are.
                            </p>
                            <p>
                                Creator management runs on commission — typically 15–20% of
                                everything your creator earns. Brand deals. Platform revenue.
                                Merchandise. Publishing. Live events. Licensing. IP.
                            </p>
                            <p className="text-ink">
                                One creator building a $500k business generates
                                $75,000–$100,000 for their manager. Every year.
                            </p>
                        </div>
                    </div>

                    <div className="lg:col-span-5">
                        <div className="border border-ink p-8 md:p-12 bg-ivory">
                            <Overline className="mb-6">Year One Example</Overline>
                            <p className="font-display text-2xl md:text-3xl leading-snug text-ink mb-8">
                                Three creators. Combined annual revenue
                                <em className="italic"> $400k.</em>
                            </p>
                            <div className="border-t border-ink-border pt-6">
                                <p className="font-mono text-xs uppercase tracking-[0.2em] text-ink-muted">
                                    Your commission @ 20%
                                </p>
                                <p className="font-display text-6xl md:text-7xl font-light text-rust tracking-tighter mt-2">
                                    $80,000
                                </p>
                            </div>
                            <p className="mt-8 font-sans text-sm text-ink-muted leading-relaxed">
                                From anywhere in the world. No team. No office. No VC money.
                                The managers who figured this out early didn’t build big
                                rosters. They built the right ones.
                            </p>
                            <p className="mt-6 font-display italic text-lg text-ink">
                                Scale is optional. Quality is not.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Price anchor */}
                <div className="mt-20 border-t border-ink-border pt-12 max-w-3xl">
                    <Overline className="mb-4">Price Anchor</Overline>
                    <p className="font-display text-2xl md:text-3xl leading-snug text-ink">
                        A university degree won’t teach you this. Most managers learn
                        through years of expensive mistakes and figuring it out alone. This
                        is <em className="italic">15 years of hard-won knowledge</em> —
                        compressed into one system.
                    </p>
                </div>
            </div>
        </section>
    );
}

// ============ The Prize ============

function ThePrize() {
    return (
        <section className="py-28 md:py-40 bg-ink text-ivory reveal">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <SectionLabel n={5}>
                    <span className="text-ivory/70">The Prize</span>
                </SectionLabel>
                <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tight max-w-5xl">
                    What can one creator{" "}
                    <em className="italic">actually be worth?</em>
                </h2>

                <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
                    <div className="lg:col-span-7 space-y-6 font-sans text-base md:text-lg leading-relaxed text-ivory/75 max-w-2xl">
                        <p>
                            Most people think creator management is a side hustle.{" "}
                            <span className="text-ivory">It’s not.</span>
                        </p>
                        <p>
                            One creator can become a six-figure business. A small roster
                            can become a seven-figure company. The right management
                            company can become an asset someone wants to acquire.
                        </p>
                        <p>
                            I’ve watched creators go from unknown to household names.
                            I’ve watched management businesses become real companies with
                            real enterprise value. I’ve lived through platform booms,
                            algorithm crashes, acquisitions, and complete industry
                            reinventions.
                        </p>
                        <p className="text-ivory">
                            What survived all of it wasn’t the biggest roster. It was the
                            deepest relationships, the smartest deal structures, and the
                            managers who understood that they were building an asset —
                            not just earning commissions.
                        </p>
                    </div>

                    <aside className="lg:col-span-5">
                        <div className="border border-ivory/20 p-8 md:p-10">
                            <Overline className="mb-6 !text-ivory/60">
                                What you’re building
                            </Overline>
                            <ul className="space-y-5">
                                <li className="font-display text-2xl md:text-3xl leading-snug text-ivory">
                                    <em className="italic text-rust">One creator</em>
                                    <br />
                                    → six-figure business
                                </li>
                                <li className="font-display text-2xl md:text-3xl leading-snug text-ivory border-t border-ivory/15 pt-5">
                                    <em className="italic text-rust">A small roster</em>
                                    <br />
                                    → seven-figure company
                                </li>
                                <li className="font-display text-2xl md:text-3xl leading-snug text-ivory border-t border-ivory/15 pt-5">
                                    <em className="italic text-rust">The right company</em>
                                    <br />
                                    → an asset someone wants to acquire
                                </li>
                            </ul>
                        </div>
                    </aside>
                </div>

                <p className="mt-16 max-w-3xl font-display italic text-2xl md:text-3xl leading-snug">
                    This course is about building something that lasts. Not a side
                    hustle. Not a job. A business with recurring revenue, real equity,
                    and the kind of value that one day someone will want to buy.
                </p>
            </div>
        </section>
    );
}

// ============ The Philosophy ============

function ThePhilosophy() {
    return (
        <section id="philosophy" className="py-28 md:py-44 reveal">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <SectionLabel n={6}>The Philosophy · The spine</SectionLabel>
                <div className="max-w-3xl mx-auto text-center">
                    <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                        Most creator managers evaluate content.
                        <br />
                        <em className="italic text-rust">The best evaluate people.</em>
                    </h2>
                </div>

                <div className="mt-16 max-w-2xl mx-auto space-y-6 font-sans text-base md:text-lg leading-relaxed text-ink-muted">
                    <p>
                        The biggest mistake in creator management is thinking you’re in the
                        content business. You’re not. You’re in the people business.
                    </p>
                    <p>
                        Every creator is a human being navigating pressure, growth, money,
                        relationships, identity, and the demands of a very public career.
                        No brand deal fixes any of that.
                    </p>
                    <p>
                        The managers who get this wrong treat creators like inventory. They
                        sign fast. Pitch hard. Scale aggressively. Then wonder why their
                        roster keeps falling apart.
                    </p>
                    <p className="text-ink">
                        The managers who get it right understand one thing most people
                        miss:{" "}
                        <span className="font-display italic text-xl text-ink">
                            who you sign matters more than how many you sign.
                        </span>
                    </p>
                    <p>
                        A creator with the right mindset, discipline, and relationship with
                        their audience can build a business for a decade. A creator with
                        great numbers and the wrong foundation becomes a full-time crisis.
                    </p>
                    <p className="font-display italic text-2xl text-ink leading-snug pt-4">
                        I can tell more in one conversation than most managers learn from
                        six months of analytics.
                    </p>
                    <p>
                        Not because I’m psychic. Because after 15 years and thousands of
                        conversations, you start to see the patterns. This masterclass
                        teaches you how to see them too.
                    </p>
                </div>
            </div>
        </section>
    );
}

// ============ What I See ============

function WhatISee() {
    return (
        <section className="py-28 md:py-40 bg-ivory-deep reveal">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <SectionLabel n={7}>What Most Managers Miss</SectionLabel>
                <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl">
                    Here’s what 15 years of conversations{" "}
                    <em className="italic">actually teaches you.</em>
                </h2>

                <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-ink-border">
                    {/* Racka Racka */}
                    <article className="bg-ivory p-8 md:p-12">
                        <div className="aspect-[4/3] overflow-hidden mb-8 bg-ink/5">
                            <img
                                src={IMG_DIRECTOR}
                                alt="Director's chair on a film set"
                                loading="lazy"
                                decoding="async"
                                className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
                            />
                        </div>
                        <Overline className="mb-4">Case Study · 01</Overline>
                        <h3 className="font-display text-3xl md:text-4xl mb-6">Racka Racka</h3>
                        <div className="space-y-4 font-sans text-base leading-relaxed text-ink-muted">
                            <p>
                                They had uploaded one video. Most people saw a YouTube
                                channel. I saw two filmmakers who needed a path, not a
                                platform.
                            </p>
                            <p>
                                In one conversation it was obvious they weren’t trying to
                                become influencers. They wanted to direct films. So we didn’t
                                build them into bigger YouTubers — we used YouTube as the
                                distribution engine and brand deals as the funding mechanism.
                            </p>
                            <p className="text-ink">
                                Every decision was designed to move them toward directing.
                                That’s exactly where they ended up.
                            </p>
                        </div>
                    </article>

                    {/* RCLbeauty101 */}
                    <article className="bg-ivory p-8 md:p-12">
                        <div className="aspect-[4/3] overflow-hidden mb-8 bg-ink/5">
                            <img
                                src={IMG_WORKSPACE}
                                alt="Editorial workspace with laptop and magazine"
                                loading="lazy"
                                decoding="async"
                                className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
                            />
                        </div>
                        <Overline className="mb-4">Case Study · 02</Overline>
                        <h3 className="font-display text-3xl md:text-4xl mb-6">RCLbeauty101</h3>
                        <div className="space-y-4 font-sans text-base leading-relaxed text-ink-muted">
                            <p>
                                Rachel found me when she was in high school making beauty
                                videos. The channel was growing. But when I watched her
                                content, I noticed something she hadn’t.
                            </p>
                            <p>
                                The beauty wasn’t why people were coming back.{" "}
                                <span className="text-ink">The comedy was.</span> Her intros
                                were more compelling than the tutorials.
                            </p>
                            <p>
                                Most managers would have doubled down on beauty. We doubled
                                down on what made her irreplaceable. That insight helped
                                create a format that defined an era of YouTube content and
                                has been copied thousands of times since.
                            </p>
                        </div>
                    </article>
                </div>

                <div className="mt-16 max-w-3xl">
                    <Overline className="mb-4">The Point</Overline>
                    <p className="font-display text-2xl md:text-3xl leading-snug text-ink">
                        In both cases the data said one thing. The person said something
                        else entirely. Learning to{" "}
                        <em className="italic">hear the person over the data</em> is the
                        skill this masterclass teaches.
                    </p>
                </div>
            </div>
        </section>
    );
}

// ============ What a creator manager actually does ============

function WhatTheyDo() {
    const items = [
        "Turn a creator’s audience into a sustainable business.",
        "Negotiate the deals they don’t know how to ask for.",
        "See the opportunities they’re too close to notice.",
        "Protect their long-term career when short-term money is on the table.",
        "Manage their time so they can focus on creating.",
        "Manage your own time so you can run a real business — not just react to chaos.",
        "Be the strategist, the negotiator, the relationship builder, and the reality check — often in the same conversation.",
    ];
    return (
        <section className="py-28 md:py-40 reveal">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <SectionLabel n={8}>The Role</SectionLabel>
                <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
                    What does a creator manager{" "}
                    <em className="italic">actually do?</em>
                </h2>

                <ol className="mt-16 max-w-4xl">
                    {items.map((t, i) => (
                        <li
                            key={i}
                            className="grid grid-cols-[60px,1fr] md:grid-cols-[100px,1fr] gap-6 py-6 border-t border-ink-border items-start"
                        >
                            <span className="font-mono text-xs md:text-sm text-rust tracking-widest pt-1">
                                {(i + 1).toString().padStart(2, "0")}
                            </span>
                            <p className="font-display text-xl md:text-2xl leading-snug text-ink">
                                {t}
                            </p>
                        </li>
                    ))}
                </ol>

                <p className="mt-16 max-w-2xl font-sans text-base md:text-lg text-ink-muted leading-relaxed">
                    The best managers are the ones creators call before they make any
                    major decision. The ones who shape careers that last — not just until
                    the algorithm changes.
                </p>
            </div>
        </section>
    );
}

// ============ How to get your first creator ============

function FirstCreator() {
    return (
        <section className="py-28 md:py-40 bg-ink text-ivory reveal">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <SectionLabel n={9}>
                    <span className="text-ivory/70">Your First Creator</span>
                </SectionLabel>
                <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl">
                    The question everyone asks: where do I{" "}
                    <em className="italic">actually find</em> my first creator?
                </h2>

                <div className="mt-12 max-w-2xl space-y-5 font-sans text-base md:text-lg leading-relaxed text-ivory/75">
                    <p>
                        Getting your first creator doesn’t require a roster, a reputation,
                        or industry connections. It requires knowing what to look for —
                        and knowing what to say when you find them.
                    </p>
                    <p>
                        I found Racka Racka from a single video. I found RCLbeauty101
                        when she was still in high school. I didn’t have industry
                        connections. I had a method.
                    </p>
                    <p>
                        The right creators are everywhere — on YouTube, TikTok,
                        Instagram, podcasts, newsletters. Most of them have never been
                        approached by a professional manager. Most of them don’t even
                        know creator management is a career.
                    </p>
                    <p className="text-ivory">
                        Module 2 of this masterclass covers exactly how to find,
                        approach, and sign your first creator — without an existing
                        network, without a track record, and from anywhere in the world.
                    </p>
                </div>
            </div>
        </section>
    );
}

// ============ AI Section ============

function AISection() {
    const cannot = [
        "Build trust with a creator",
        "Read the room in a first conversation",
        "Spot the red flags before you sign someone",
        "See the filmmaker hiding inside the YouTuber",
    ];
    const can = [
        "Research brands and analyse deals at speed",
        "Draft contracts, emails, and proposals in minutes",
        "Run your entire back-end operation",
        "Free up hours every week so you focus on what actually matters",
    ];
    return (
        <section className="py-28 md:py-40 reveal">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <SectionLabel n={10}>On AI</SectionLabel>
                <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl">
                    Everyone is using AI. Most are{" "}
                    <em className="italic">automating the wrong things.</em>
                </h2>

                <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-ink-border border border-ink-border">
                    <div className="bg-ivory p-8 md:p-12">
                        <Overline className="mb-6 !text-rust">AI Cannot</Overline>
                        <ul className="space-y-5">
                            {cannot.map((t, i) => (
                                <li
                                    key={i}
                                    className="flex items-start gap-4 font-display text-xl md:text-2xl text-ink leading-snug"
                                >
                                    <span className="font-mono text-xs text-rust mt-2">
                                        ×
                                    </span>
                                    <span>{t}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="bg-ivory-deep p-8 md:p-12">
                        <Overline className="mb-6">AI Can</Overline>
                        <ul className="space-y-5">
                            {can.map((t, i) => (
                                <li
                                    key={i}
                                    className="flex items-start gap-4 font-display text-xl md:text-2xl text-ink leading-snug"
                                >
                                    <span className="font-mono text-xs text-ink mt-2">
                                        ◆
                                    </span>
                                    <span>{t}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="mt-16 max-w-3xl">
                    <p className="font-display text-2xl md:text-3xl leading-snug text-ink">
                        The managers winning right now aren’t the ones with the biggest AI
                        stack. They’re the ones who know{" "}
                        <em className="italic">what should never be automated.</em>
                    </p>
                    <p className="mt-6 font-sans text-base text-ink-muted leading-relaxed">
                        The conversations. The judgment. The relationships. Module 9
                        covers the exact AI toolkit for running a lean, high-value
                        management business — what to use, what to avoid, and how to
                        operate without a large team.
                    </p>
                </div>
            </div>
        </section>
    );
}

// ============ Three Pillars ============

function ThreePillars() {
    const pillars = [
        {
            n: "I.",
            title: "Find and evaluate",
            sub: "the right creators",
            points: [
                "How to identify talent before the market does.",
                "How to read a creator in one conversation.",
                "The Three C’s — choosing, chasing, and chopping talent.",
                "The red flags most managers miss entirely.",
            ],
        },
        {
            n: "II.",
            title: "Build and operate",
            sub: "the agency",
            points: [
                "Set up your company legally and professionally.",
                "Structure agreements that protect you and your creators.",
                "Negotiate brand deals and platform partnerships.",
                "Manage creator relationships. Use AI to run lean.",
            ],
        },
        {
            n: "III.",
            title: "Build enterprise value",
            sub: "and one day exit",
            points: [
                "Recurring revenue from an intentional roster.",
                "Commission structures, deal flow, long-term income.",
                "Build a business with real equity — one that could one day be acquired.",
                "Nobody else is teaching this. Because nobody else has done it.",
            ],
        },
    ];

    return (
        <section className="py-28 md:py-40 bg-ivory-deep reveal">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <SectionLabel n={11}>The Architecture</SectionLabel>
                <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
                    This masterclass is built around{" "}
                    <em className="italic">three pillars.</em>
                </h2>

                <p className="mt-10 max-w-2xl font-sans text-base md:text-lg leading-relaxed text-ink-muted">
                    Most people who try to build a creator management business fail at
                    one of three points: they sign the wrong creators, they don’t know
                    how to run the business properly, or they build revenue without
                    building real value. This masterclass covers all three.
                </p>

                <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-ink-border border border-ink-border">
                    {pillars.map((p, i) => (
                        <div key={i} className="bg-ivory p-8 md:p-10 flex flex-col">
                            <span className="font-display italic text-5xl text-rust mb-8">
                                {p.n}
                            </span>
                            <h3 className="font-display text-3xl md:text-4xl text-ink leading-tight">
                                {p.title}
                            </h3>
                            <p className="font-display italic text-lg text-ink-muted mt-1">
                                {p.sub}
                            </p>
                            <ul className="mt-8 space-y-3 font-sans text-sm md:text-base text-ink-muted leading-relaxed">
                                {p.points.map((pt, j) => (
                                    <li key={j} className="flex gap-3">
                                        <span className="text-rust">—</span>
                                        <span>{pt}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                <p className="mt-12 max-w-3xl font-display text-xl md:text-2xl text-ink italic leading-snug">
                    Most creator courses teach Pillar 2 basics. Some touch Pillar 1.
                    Nobody else is teaching Pillar 3. Because nobody else has done all
                    three and sold the company.
                </p>
            </div>
        </section>
    );
}

// ============ What You'll Learn ============

function WhatYoullLearn() {
    const items = [
        "Find creators before the market does — and know whether they’re worth signing.",
        "Have the conversation that tells you everything you need to know.",
        "Set up your management company legally and professionally.",
        "Structure agreements that protect you and your creators.",
        "Negotiate brand deals and platform partnerships.",
        "Build recurring revenue from a small, intentional roster.",
        "Manage creator relationships without losing the professional boundary.",
        "Use AI to run lean without losing the human edge.",
        "Build something with real long-term value — and one day exit.",
    ];
    return (
        <section className="py-28 md:py-40 reveal">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <SectionLabel n={12}>Outcomes</SectionLabel>
                <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
                    By the end of this masterclass{" "}
                    <em className="italic">you will know how to:</em>
                </h2>
                <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6 max-w-5xl">
                    {items.map((t, i) => (
                        <div key={i} className="flex gap-4 py-4 border-t border-ink-border">
                            <span className="font-mono text-xs text-rust pt-1.5">
                                {(i + 1).toString().padStart(2, "0")}
                            </span>
                            <p className="font-display text-xl md:text-2xl text-ink leading-snug">
                                {t}
                            </p>
                        </div>
                    ))}
                </div>
                <p className="mt-14 max-w-2xl font-display italic text-2xl md:text-3xl text-ink leading-snug">
                    Build this from London, Lagos, Sydney, Dubai, Toronto, São Paulo,
                    Oslo — or anywhere else. Most markets have no professionally trained
                    creator managers. Whoever builds it properly first wins.
                </p>
            </div>
        </section>
    );
}

// ============ Who This Is For ============

function WhoThisIsFor() {
    const personas = [
        {
            title: "Building a creator management business from scratch.",
            body: "No experience required. This is the complete system from day one.",
            big: true,
        },
        {
            title: "Existing managers figuring it out alone.",
            body: "You have creators. You have questions nobody is answering. This fills the gaps.",
        },
        {
            title: "Talent agents from music, sport, film, or PR.",
            body: "You already understand management. This teaches you how those principles apply in the creator economy — and which ones need to be completely rebuilt.",
        },
        {
            title: "Creator economy professionals who want to run their own business.",
            body: "You work at an agency, a platform, or a brand. You understand creators. This is your path to building something of your own.",
        },
        {
            title: "Creators transitioning to the other side of the camera.",
            body: "You don’t want to be on camera forever. You want to build something behind it. This is how.",
        },
    ];

    return (
        <section className="py-28 md:py-40 bg-ivory-deep reveal">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <SectionLabel n={13}>Who This Is For</SectionLabel>
                <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
                    This course was built for{" "}
                    <em className="italic">five types of people.</em>
                </h2>

                <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-px bg-ink-border border border-ink-border">
                    {personas.map((p, i) => (
                        <article
                            key={i}
                            className={`bg-ivory p-8 md:p-10 ${
                                p.big ? "md:col-span-12" : "md:col-span-6"
                            }`}
                        >
                            <div className="flex items-baseline gap-4 mb-6">
                                <span className="font-mono text-xs text-rust tracking-widest">
                                    {(i + 1).toString().padStart(2, "0")}
                                </span>
                                <span className="h-px flex-1 bg-ink-border" />
                            </div>
                            <h3
                                className={`font-display text-ink leading-tight ${
                                    p.big ? "text-3xl md:text-5xl" : "text-2xl md:text-3xl"
                                }`}
                            >
                                {p.title}
                            </h3>
                            <p className="mt-4 font-sans text-base text-ink-muted leading-relaxed max-w-xl">
                                {p.body}
                            </p>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ============ Who This Is Not For ============

function WhoThisIsNotFor() {
    const items = [
        "You want to sign as many creators as possible as fast as possible.",
        "You think AI outreach tools will build your business for you.",
        "You see creators as a product to sell rather than a person to understand.",
        "You want overnight results with no real work behind them.",
        "You’re looking for a passive income course.",
    ];
    return (
        <section className="py-28 md:py-40 bg-ink text-ivory reveal">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <SectionLabel n={14}>
                    <span className="text-ivory/70">Not For You If</span>
                </SectionLabel>
                <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
                    This is <em className="italic">not for you</em> if:
                </h2>
                <ul className="mt-14 max-w-4xl">
                    {items.map((t, i) => (
                        <li
                            key={i}
                            className="grid grid-cols-[60px,1fr] md:grid-cols-[100px,1fr] gap-6 py-7 border-t border-ivory/15"
                        >
                            <span className="font-mono text-xs text-ivory/40 tracking-widest pt-2">
                                {(i + 1).toString().padStart(2, "0")}
                            </span>
                            <p className="font-display text-xl md:text-2xl leading-snug text-ivory/90">
                                {t}
                            </p>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    );
}

// ============ Why Naomi ============

function WhyNaomi() {
    const proof = [
        "15+ years managing creators",
        "6,000+ brand partnerships negotiated",
        "Founder of Lennon Management — acquired 2019",
        "Former Studio71 and Brillstein Entertainment Partners",
        "Currently retained by clients in AI, SaaS, and creator technology — undisclosed",
        "Consulted across North America, Europe, Australia, and the Middle East",
        "Speaker: VidCon, Cannes Lions, OMR Germany, RedBang China",
        "2026 keynotes: Creator Economy Live Las Vegas · Billion Follower Conference Dubai",
    ];

    return (
        <section id="why-naomi" className="py-28 md:py-40 reveal">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <SectionLabel n={15}>Why Naomi</SectionLabel>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                    <div className="lg:col-span-7">
                        <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                            Most people teaching this built their business during one
                            platform era.{" "}
                            <em className="italic">I’ve spent 15 years surviving all of them.</em>
                        </h2>

                        <div className="mt-12 space-y-8 max-w-2xl font-sans text-base md:text-lg leading-relaxed text-ink-muted">
                            <div>
                                <Overline className="mb-3">Hollywood Bridge</Overline>
                                <p>
                                    I learned talent management in Hollywood — trained under
                                    Marty Bauer, one of the founders of United Talent Agency.
                                    Then I bet everything on a world that didn’t exist yet.
                                </p>
                            </div>
                            <div>
                                <Overline className="mb-3">Origin Story</Overline>
                                <p>
                                    I left a Hollywood talent agency to manage YouTube
                                    creators when most of the entertainment industry thought
                                    it was a joke. My boss told me YouTube clients would
                                    never do better than John Stamos. I left anyway.
                                </p>
                                <p className="mt-3">
                                    There were no courses, no playbooks, no conferences, no
                                    career paths. We were inventing the rules in real time.
                                    Every deal was the first of its kind.
                                </p>
                            </div>
                            <div>
                                <Overline className="mb-3">Platform Survival</Overline>
                                <p>
                                    I’ve built creator businesses across YouTube, Vine,
                                    Instagram, TikTok, and Snapchat. Every platform shift.
                                    Every algorithm change. I’ve navigated all of it.
                                </p>
                            </div>
                            <div>
                                <Overline className="mb-3">Credentials</Overline>
                                <p>
                                    I worked with Logan Paul, RCLbeauty101, Racka Racka,
                                    King Bach, and creators who helped shape what the
                                    internet became. I negotiated deals with YouTube,
                                    Meta, TikTok, and Snapchat that changed how the
                                    industry worked.
                                </p>
                            </div>
                            <div className="border-l-2 border-rust pl-6">
                                <Overline className="mb-3 !text-rust">The Acquisition</Overline>
                                <p className="font-display text-2xl md:text-3xl text-ink leading-snug">
                                    In 2019, the creator management company I built was
                                    acquired. That proved something I’d believed for a
                                    decade: creator management isn’t a side hustle. It’s a
                                    real business. <em className="italic">The kind that gets bought.</em>
                                </p>
                            </div>
                            <div>
                                <Overline className="mb-3">Active Operator</Overline>
                                <p>
                                    Since then I’ve consulted for publishers, AI
                                    companies, and global brands on creator strategy
                                    across four continents. I currently build and scale
                                    influencer marketing programmes for active clients
                                    across AI, SaaS, and creator technology.
                                </p>
                                <p className="mt-3 font-display italic text-xl text-ink">
                                    I’m not teaching theory from the sidelines. I’m still
                                    in the room.
                                </p>
                            </div>
                            <div>
                                <Overline className="mb-3">Why I’m Teaching This</Overline>
                                <p>
                                    For 15 years, everything I know was passed through closed
                                    networks, private agency relationships, and expensive
                                    trial and error. Nobody taught me. I had to figure it
                                    out. This is the course I wish existed when I started.
                                </p>
                            </div>
                        </div>
                    </div>

                    <aside className="lg:col-span-5 lg:sticky lg:top-24">
                        <div className="border border-ink-border bg-ivory-deep">
                            <div className="aspect-[4/5] overflow-hidden">
                                <img
                                    src={IMG_CREW}
                                    alt="Behind the scenes — directors and crew"
                                    loading="lazy"
                                    decoding="async"
                                    className="w-full h-full object-cover grayscale"
                                />
                            </div>
                            <div className="p-8">
                                <Overline className="mb-5">Proof</Overline>
                                <ul className="space-y-3 font-sans text-sm text-ink-muted leading-relaxed">
                                    {proof.map((t, i) => (
                                        <li key={i} className="flex gap-3">
                                            <span className="text-rust">—</span>
                                            <span>{t}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </section>
    );
}

// ============ Advisory & Consulting ============

function AdvisoryConsulting({ scrollToEnquiries }) {
    const strategic = [
        "Creator economy strategy and market positioning",
        "Creator partnership and talent programme design",
        "Platform strategy across YouTube, TikTok, Instagram, and emerging platforms",
        "AI integration in creator operations",
        "Creator-led content and IP development",
        "Go-to-market strategy for creator-facing products",
    ];
    const influencer = [
        "Creator acquisition strategy and execution",
        "Influencer programme design and operations",
        "Performance creator campaigns",
        "Creator partnership measurement and optimisation",
        "Agency relationships and management",
    ];

    return (
        <section id="advisory" className="bg-ink text-ivory py-28 md:py-40 reveal scroll-mt-20">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="flex items-baseline gap-4 mb-10 md:mb-16">
                    <span className="font-mono text-xs text-ivory/50 tracking-widest">
                        § 16
                    </span>
                    <span className="h-px flex-1 bg-ivory/20" />
                    <p className="font-mono text-[10px] md:text-xs uppercase tracking-[0.3em] text-ivory/70">
                        Work with Naomi
                    </p>
                </div>

                <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-5xl">
                    For 15 years I’ve built businesses{" "}
                    <em className="italic">inside the creator economy.</em> Not around
                    it.
                </h2>

                <p className="mt-10 max-w-2xl font-sans text-base md:text-lg leading-relaxed text-ivory/75">
                    I work with AI companies, publishers, creator platforms, brands, and
                    founder-led businesses that need the kind of thinking that only
                    comes from actually building in this space.
                </p>

                <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-ivory/15 border border-ivory/15">
                    {/* LEFT — Strategic Advisory */}
                    <div className="bg-ink p-8 md:p-12">
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-rust mb-5">
                            Strategic Advisory
                        </p>
                        <h3 className="font-display text-2xl md:text-3xl text-ivory leading-snug mb-3">
                            For organisations navigating the creator economy.
                        </h3>
                        <ul className="mt-8 space-y-4 font-sans text-sm md:text-base text-ivory/75 leading-relaxed">
                            {strategic.map((t, i) => (
                                <li key={i} className="flex gap-3">
                                    <span className="text-rust">—</span>
                                    <span>{t}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    {/* RIGHT — Influencer Growth */}
                    <div className="bg-ink p-8 md:p-12">
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-rust mb-5">
                            Influencer Growth
                        </p>
                        <h3 className="font-display text-2xl md:text-3xl text-ivory leading-snug mb-3">
                            Build and scale creator acquisition, ambassador, and
                            influencer marketing programmes.
                        </h3>
                        <ul className="mt-8 space-y-4 font-sans text-sm md:text-base text-ivory/75 leading-relaxed">
                            {influencer.map((t, i) => (
                                <li key={i} className="flex gap-3">
                                    <span className="text-rust">—</span>
                                    <span>{t}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-10">
                    <div>
                        <Overline className="mb-3 !text-ivory/60">
                            Currently Retained By
                        </Overline>
                        <p className="font-display text-xl md:text-2xl text-ivory leading-snug">
                            Clients in AI, SaaS, and creator technology — undisclosed.
                        </p>
                    </div>
                    <div>
                        <Overline className="mb-3 !text-ivory/60">
                            Engagement Types
                        </Overline>
                        <p className="font-sans text-base md:text-lg text-ivory/80 leading-relaxed">
                            Advisory retainers · Strategy sprints · Workshops · Keynotes
                            · Project-based consulting
                        </p>
                    </div>
                </div>

                <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-6">
                    <button
                        type="button"
                        onClick={scrollToEnquiries}
                        data-testid={LANDING.advisoryContactBtn}
                        className="inline-flex items-center justify-center bg-ivory text-ink font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 hover:bg-rust hover:text-ivory transition-colors duration-300"
                    >
                        Get in touch →
                    </button>
                    <a
                        href="mailto:naomi@naomilennon.com"
                        data-testid={LANDING.advisoryEmailLink}
                        className="font-mono text-[11px] uppercase tracking-[0.25em] text-ivory/80 hover:text-rust transition-colors"
                    >
                        naomi@naomilennon.com
                    </a>
                </div>
            </div>
        </section>
    );
}

// ============ Timeline ============

function Timeline() {
    const entries = [
        {
            year: "2009",
            text: "Learned talent management inside Hollywood under one of the founders of United Talent Agency.",
        },
        {
            year: "2010",
            text: "Left to represent YouTube creators when the entire entertainment industry thought it was a hobby. My boss said it would never work. I left anyway.",
        },
        {
            year: "2011 — 2015",
            text: "Built creator businesses across YouTube, Vine, and Instagram. Spotted Racka Racka from a single video. Helped RCLbeauty101 invent a genre. Negotiated some of the first-ever platform deals in the industry.",
        },
        {
            year: "2016 — 2019",
            text: "Scaled Lennon Management into a global creator agency. Watched the industry explode. Kept doing it properly.",
        },
        {
            year: "2019",
            text: "The agency I built was acquired.",
            accent: true,
        },
        {
            year: "2019 — Today",
            text: "Consulted for publishers, AI companies, and global brands across four continents. Watched AI enter the space. Figured out what it’s actually useful for.",
        },
        {
            year: "Today",
            text: "Teaching everything I know — for the first time.",
            accent: true,
        },
    ];

    return (
        <section className="py-28 md:py-40 bg-ivory-deep reveal">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <SectionLabel n={17}>Timeline</SectionLabel>
                <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
                    I was there before{" "}
                    <em className="italic">there was a playbook.</em>
                </h2>

                <ol className="mt-16 max-w-5xl">
                    {entries.map((e, i) => (
                        <li
                            key={i}
                            className="grid grid-cols-[1fr,3fr] gap-6 md:gap-12 py-8 border-t border-ink-border"
                        >
                            <span
                                className={`font-mono text-sm md:text-base tracking-widest ${
                                    e.accent ? "text-rust" : "text-ink"
                                }`}
                            >
                                {e.year}
                            </span>
                            <p
                                className={`font-display leading-snug ${
                                    e.accent
                                        ? "text-ink text-2xl md:text-3xl italic"
                                        : "text-ink-muted text-xl md:text-2xl"
                                }`}
                            >
                                {e.text}
                            </p>
                        </li>
                    ))}
                </ol>
            </div>
        </section>
    );
}

// ============ Curriculum ============

function Curriculum() {
    const pillars = [
        {
            label: "Pillar I — Find and Evaluate",
            modules: [
                "01 — What creator management actually is — and what nobody tells you.",
                "02 — The conversation that determines everything — how to read a creator before you sign.",
                "03 — The three C’s — choosing, chasing, and chopping talent.",
            ],
        },
        {
            label: "Pillar II — Build and Operate",
            modules: [
                "04 — Setting up your company — structure, legal, and operations.",
                "05 — Negotiating deals — 15 years of lessons from the room.",
                "06 — Managing creator relationships — the human side nobody talks about.",
                "07 — Brand partnerships, platform deals, and revenue streams.",
            ],
        },
        {
            label: "Pillar III — Build Enterprise Value",
            modules: [
                "08 — Building from scrappy to scalable — the intentional roster.",
                "09 — The AI toolkit — what to automate, what to protect, how to run lean.",
            ],
        },
    ];

    return (
        <section id="curriculum" className="py-28 md:py-40 reveal">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <SectionLabel n={18}>Curriculum</SectionLabel>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                    <div className="lg:col-span-7">
                        <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                            9 modules. Everything you need.{" "}
                            <em className="italic">Nothing you don’t.</em>
                        </h2>
                        <p className="mt-8 max-w-xl font-sans text-base md:text-lg leading-relaxed text-ink-muted">
                            Built from 15 years of real deals, real talent, and real
                            mistakes. Not theory. What actually happens in creator
                            management — and how to handle it.
                        </p>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="aspect-[5/4] overflow-hidden border border-ink-border">
                            <img
                                src={IMG_CURRICULUM}
                                alt="Top view of camera and laptop on desk"
                                loading="lazy"
                                decoding="async"
                                className="w-full h-full object-cover grayscale-[40%]"
                            />
                        </div>
                    </div>
                </div>

                <div className="mt-16 space-y-12">
                    {pillars.map((p, i) => (
                        <div key={i}>
                            <Overline className="mb-6">{p.label}</Overline>
                            <ul className="border-t border-ink-border">
                                {p.modules.map((m, j) => (
                                    <li
                                        key={j}
                                        className="py-6 border-b border-ink-border font-display text-2xl md:text-3xl text-ink leading-snug"
                                    >
                                        {m}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ============ Founding Cohort (replaces testimonials) ============

function FoundingCohort({ scrollToPricing }) {
    return (
        <section className="py-28 md:py-40 bg-ivory-deep reveal">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <SectionLabel n={19}>Testimonials</SectionLabel>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
                    <div className="lg:col-span-7">
                        <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                            Real student stories{" "}
                            <em className="italic">coming at launch.</em>
                        </h2>
                        <div className="mt-10 max-w-xl space-y-5 font-sans text-base md:text-lg leading-relaxed text-ink-muted">
                            <p>
                                This masterclass launches with a founding member cohort.
                                Testimonials from real students will be published here as
                                they build their businesses.
                            </p>
                            <p className="text-ink">
                                If you want to be part of the founding cohort — and be one
                                of the first voices featured here — claim a founding seat
                                below.
                            </p>
                        </div>
                        <div className="mt-10">
                            <PrimaryButton
                                onClick={scrollToPricing}
                                testId={LANDING.foundingCohortBtn}
                            >
                                Claim your founding seat — $497
                            </PrimaryButton>
                        </div>
                    </div>

                    <aside className="lg:col-span-5">
                        <div className="border border-ink-border bg-ivory p-10 md:p-12 relative">
                            <span className="absolute -top-3 left-8 bg-rust text-ivory font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-1">
                                Founding Cohort
                            </span>
                            <Overline className="mb-6">First 30 students</Overline>
                            <p className="font-display text-2xl md:text-3xl leading-snug text-ink">
                                Your name. Your story. Your business.{" "}
                                <em className="italic text-rust">Featured here.</em>
                            </p>
                            <div className="mt-8 pt-6 border-t border-ink-border space-y-3 font-sans text-sm text-ink-muted leading-relaxed">
                                <p className="flex gap-3"><span className="text-rust">—</span><span>Direct line to the cohort</span></p>
                                <p className="flex gap-3"><span className="text-rust">—</span><span>First students featured on this page</span></p>
                                <p className="flex gap-3"><span className="text-rust">—</span><span>$497 — locked at $997 once full</span></p>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </section>
    );
}

// ============ Become ============

function Become() {
    const before = [
        "Unsure which creators to sign — or when to walk away.",
        "Guessing on contracts and deal structures.",
        "Learning through expensive mistakes.",
        "Chasing volume instead of building value.",
    ];
    const after = [
        "Reading talent from the first conversation.",
        "Structuring deals with real confidence.",
        "Running lean with the right AI tools.",
        "Building recurring revenue from an intentional roster.",
        "Operating like the professional you always intended to be.",
    ];
    return (
        <section className="py-28 md:py-40 reveal">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <SectionLabel n={20}>What You’ll Become</SectionLabel>
                <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-5xl">
                    After this masterclass, you’ll know how to spot talent, negotiate
                    deals, and build a creator management company{" "}
                    <em className="italic">worth owning.</em>
                </h2>

                <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-ink-border border border-ink-border">
                    <div className="bg-ivory-deep p-8 md:p-12">
                        <Overline className="mb-8">Before</Overline>
                        <ul className="space-y-5">
                            {before.map((t, i) => (
                                <li key={i} className="font-display text-xl md:text-2xl text-ink-muted leading-snug">
                                    — {t}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="bg-ivory p-8 md:p-12">
                        <Overline className="mb-8 !text-rust">After</Overline>
                        <ul className="space-y-5">
                            {after.map((t, i) => (
                                <li key={i} className="font-display text-xl md:text-2xl text-ink leading-snug">
                                    — {t}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <p className="mt-14 max-w-3xl font-display italic text-2xl md:text-3xl text-ink leading-snug">
                    Not the one signing everyone and hoping something sticks. The one who
                    sees what others miss. The connector. The negotiator. The trusted
                    advisor. The person creators call before they make any decision that
                    matters.
                </p>
                <p className="mt-6 max-w-3xl font-sans text-base md:text-lg text-ink-muted leading-relaxed">
                    A real business. Recurring revenue. Real equity. The kind of value
                    that one day you can sell.
                </p>
            </div>
        </section>
    );
}

// ============ Future Vision ============

function FutureVision() {
    return (
        <section className="py-32 md:py-48 bg-ink text-ivory relative grain overflow-hidden">
            <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
                <SectionLabel n={21}>
                    <span className="text-ivory/70">Future Vision</span>
                </SectionLabel>
                <h2 className="font-display font-light text-5xl sm:text-6xl md:text-7xl leading-[1.02] tracking-tighter max-w-5xl">
                    Ten years from now, every major market will have{" "}
                    <em className="italic">creator management firms.</em>
                </h2>

                <div className="mt-12 max-w-2xl space-y-5 font-sans text-base md:text-lg leading-relaxed text-ivory/75">
                    <p>
                        The question is whether you’ll be one of the people who built
                        them.
                    </p>
                    <p>
                        The creators are already there. The brands are already spending.
                        The deals are already happening. What’s missing is managers who
                        know what they’re doing.
                    </p>
                    <p>
                        Not people using AI to blast outreach. Not people signing anyone
                        with a following. Managers who understand creators — really
                        understand them — and know how to build sustainable businesses
                        around them.
                    </p>
                    <p className="text-ivory">
                        In 2010 I believed in this industry before it existed. I’m still
                        here. And the opportunity has never been bigger.
                    </p>
                </div>

                <p className="mt-16 font-display italic text-3xl md:text-5xl leading-tight max-w-4xl">
                    Sign slower. Understand deeper. Build bigger.
                </p>
            </div>
        </section>
    );
}

// ============ The Weekly (between Future Vision and Guarantee) ============

function TheWeekly() {
    const [email, setEmail] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            await axios.post(`${API}/waitlist`, { email, source: "weekly" });
            setSubmitted(true);
            toast.success("You’re subscribed.");
        } catch (err) {
            toast.error("Couldn’t subscribe — please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="weekly" className="py-28 md:py-36 bg-ivory-deep reveal scroll-mt-20">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="max-w-3xl">
                    <Overline className="mb-6">The Weekly</Overline>
                    <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                        Learn what actually works in{" "}
                        <em className="italic">creator management.</em>
                    </h2>
                    <p className="mt-8 font-sans text-base md:text-lg leading-relaxed text-ink-muted max-w-2xl">
                        One email a week. Fifteen years of lessons — what to sign, what
                        to walk away from, how the deals actually get done, and where
                        the creator economy is going next. No courses. No pitches. Just
                        the stuff worth knowing.
                    </p>

                    <form
                        onSubmit={onSubmit}
                        className="mt-10 flex flex-col sm:flex-row gap-3 max-w-lg"
                    >
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Your email address"
                            disabled={submitted}
                            data-testid={LANDING.weeklyEmailInput}
                            className="flex-1 bg-transparent border-b border-ink px-2 py-3 font-sans text-base text-ink placeholder:text-ink-muted/60 focus:outline-none focus:border-rust transition-colors disabled:opacity-60"
                        />
                        <button
                            type="submit"
                            disabled={loading || submitted}
                            data-testid={LANDING.weeklySubmitBtn}
                            className="bg-ink text-ivory font-mono text-xs uppercase tracking-[0.2em] px-6 py-3 hover:bg-rust transition-colors disabled:opacity-60 whitespace-nowrap"
                        >
                            {submitted
                                ? "Subscribed"
                                : loading
                                ? "…"
                                : "Send me the weekly"}
                        </button>
                    </form>
                    {submitted && (
                        <p
                            data-testid={LANDING.weeklySuccess}
                            className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-rust"
                        >
                            You’re subscribed. First email on the way.
                        </p>
                    )}
                    <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted/70">
                        One email per week. No spam. Unsubscribe anytime.
                    </p>
                </div>
            </div>
        </section>
    );
}

// ============ Guarantee ============

function Guarantee() {
    return (
        <section className="py-28 md:py-40 reveal">
            <div className="max-w-3xl mx-auto px-6 md:px-12">
                <div className="border border-ink p-10 md:p-14 text-center relative">
                    <Overline className="mb-6">The Guarantee</Overline>
                    <h2 className="font-display font-light text-3xl md:text-5xl leading-tight text-ink mb-6">
                        Complete the masterclass. If it wasn’t worth{" "}
                        <em className="italic">every penny</em> — full refund within 30 days.
                    </h2>
                    <p className="font-sans text-base text-ink-muted leading-relaxed max-w-md mx-auto">
                        No questions. I’ve spent 15 years building this. I’m not worried
                        about the guarantee.
                    </p>
                </div>
            </div>
        </section>
    );
}

// ============ Pricing ============

function Pricing() {
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [loading, setLoading] = useState(null); // 'founding' | 'standard' | null

    const startCheckout = async (packageId) => {
        setLoading(packageId);
        try {
            const origin = window.location.origin;
            const { data } = await axios.post(`${API}/checkout/session`, {
                package_id: packageId,
                origin_url: origin,
                email: email || null,
                name: name || null,
            });
            if (data?.url) {
                window.location.href = data.url;
            } else {
                throw new Error("No checkout URL");
            }
        } catch (err) {
            toast.error("We couldn’t start checkout. Please try again.");
            setLoading(null);
        }
    };

    const features = [
        "9 video modules across three pillars",
        "Contract templates and cold-email scripts",
        "The creator manager’s AI toolkit",
        "Time-management framework for managers",
        "Real case studies — Racka Racka, RCLbeauty101",
        "Lifetime access and updates",
        "30-day money-back guarantee",
    ];

    return (
        <section id="pricing" className="py-28 md:py-40 bg-ivory-deep reveal">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <SectionLabel n={22}>Enrol</SectionLabel>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                    <div className="lg:col-span-5">
                        <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                            You don’t need Hollywood. You don’t need a massive roster.{" "}
                            <em className="italic">You need the right system.</em>
                        </h2>
                        <p className="mt-8 font-sans text-base md:text-lg leading-relaxed text-ink-muted max-w-md">
                            And the judgment to use it. One-time payment. Lifetime access.
                            30-day money-back guarantee.
                        </p>

                        <div className="mt-12 space-y-3 max-w-md">
                            <input
                                type="text"
                                placeholder="Your name (optional)"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                data-testid={LANDING.pricingNameInput}
                                className="w-full bg-transparent border-b border-ink px-2 py-3 font-sans text-base text-ink placeholder:text-ink-muted/60 focus:outline-none focus:border-rust transition-colors"
                            />
                            <input
                                type="email"
                                placeholder="your@email.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid={LANDING.pricingEmailInput}
                                className="w-full bg-transparent border-b border-ink px-2 py-3 font-sans text-base text-ink placeholder:text-ink-muted/60 focus:outline-none focus:border-rust transition-colors"
                            />
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted/70">
                                We’ll send your access details here after payment.
                            </p>
                        </div>
                    </div>

                    {/* Founding card */}
                    <div className="lg:col-span-4">
                        <div className="border border-ink bg-ivory p-8 md:p-10 h-full flex flex-col relative">
                            <span className="absolute -top-3 left-8 bg-rust text-ivory font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-1">
                                Founding Member
                            </span>
                            <Overline className="mb-3">First 30 students only</Overline>
                            <div className="flex items-baseline gap-3">
                                <span className="font-display font-light text-7xl md:text-8xl text-ink tracking-tighter leading-none">
                                    $497
                                </span>
                                <span className="font-mono text-xs text-ink-muted/70 line-through">
                                    $997
                                </span>
                            </div>
                            <p className="mt-3 font-sans text-sm text-ink-muted">
                                One-time payment · Lifetime access
                            </p>
                            <ul className="mt-8 space-y-3 font-sans text-sm text-ink-muted leading-relaxed flex-1">
                                {features.map((f, i) => (
                                    <li key={i} className="flex gap-3">
                                        <span className="text-rust">—</span>
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                            <PrimaryButton
                                onClick={() => startCheckout("founding")}
                                testId={LANDING.pricingFoundingBtn}
                                className="mt-10 w-full"
                            >
                                {loading === "founding"
                                    ? "Opening Stripe…"
                                    : "Claim founding seat — $497"}
                            </PrimaryButton>
                        </div>
                    </div>

                    {/* Standard card */}
                    <div className="lg:col-span-3">
                        <div className="border border-ink-border bg-ivory p-8 h-full flex flex-col">
                            <Overline className="mb-3">Standard</Overline>
                            <span className="font-display font-light text-6xl text-ink tracking-tighter leading-none">
                                $997
                            </span>
                            <p className="mt-3 font-sans text-sm text-ink-muted">
                                Once founding seats are gone.
                            </p>
                            <ul className="mt-8 space-y-3 font-sans text-sm text-ink-muted leading-relaxed flex-1">
                                <li>— Same 9-module curriculum</li>
                                <li>— Lifetime access and updates</li>
                                <li>— 30-day money-back guarantee</li>
                            </ul>
                            <SecondaryButton
                                onClick={() => startCheckout("standard")}
                                testId={LANDING.pricingStandardBtn}
                                className="mt-10 w-full"
                            >
                                {loading === "standard" ? "Opening Stripe…" : "Join at standard price — $997"}
                            </SecondaryButton>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

// ============ Enquiries (Speaking, Brand, Press) ============

function Enquiries() {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [type, setType] = useState("speaking");
    const [message, setMessage] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!name || !email || !message) return;
        setLoading(true);
        try {
            await axios.post(`${API}/enquiry`, { name, email, type, message });
            setSubmitted(true);
            toast.success("Enquiry received. Naomi will be in touch.");
        } catch (err) {
            toast.error("Couldn’t send — please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="enquiries" className="py-28 md:py-36 reveal scroll-mt-20">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                    <div className="lg:col-span-5">
                        <Overline className="mb-6">Speaking & Enquiries</Overline>
                        <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                            For everything else,{" "}
                            <em className="italic">write directly.</em>
                        </h2>
                        <p className="mt-8 max-w-md font-sans text-base md:text-lg leading-relaxed text-ink-muted">
                            Keynotes, fireside chats, panels, brand consulting, press
                            requests, and partnership conversations. Tell me what you’re
                            building and I’ll get back personally.
                        </p>
                    </div>

                    <div className="lg:col-span-7">
                        <form
                            onSubmit={onSubmit}
                            className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6"
                        >
                            <div className="md:col-span-1">
                                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted mb-2">
                                    Name
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    disabled={submitted}
                                    data-testid={LANDING.enquiryNameInput}
                                    className="w-full bg-transparent border-b border-ink px-1 py-3 font-sans text-base text-ink placeholder:text-ink-muted/60 focus:outline-none focus:border-rust transition-colors disabled:opacity-60"
                                />
                            </div>
                            <div className="md:col-span-1">
                                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted mb-2">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={submitted}
                                    data-testid={LANDING.enquiryEmailInput}
                                    className="w-full bg-transparent border-b border-ink px-1 py-3 font-sans text-base text-ink placeholder:text-ink-muted/60 focus:outline-none focus:border-rust transition-colors disabled:opacity-60"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted mb-2">
                                    Nature of enquiry
                                </label>
                                <select
                                    value={type}
                                    onChange={(e) => setType(e.target.value)}
                                    disabled={submitted}
                                    data-testid={LANDING.enquiryTypeSelect}
                                    className="w-full bg-transparent border-b border-ink px-1 py-3 font-sans text-base text-ink focus:outline-none focus:border-rust transition-colors disabled:opacity-60 appearance-none"
                                    style={{
                                        backgroundImage:
                                            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23171614' stroke-width='2'><polyline points='6 9 12 15 18 9'/></svg>\")",
                                        backgroundRepeat: "no-repeat",
                                        backgroundPosition: "right 0.25rem center",
                                        paddingRight: "1.5rem",
                                    }}
                                >
                                    <option value="speaking">Speaking engagement</option>
                                    <option value="brand">Brand consulting</option>
                                    <option value="press">Press / media</option>
                                    <option value="other">Something else</option>
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted mb-2">
                                    Message
                                </label>
                                <textarea
                                    required
                                    rows={5}
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    disabled={submitted}
                                    data-testid={LANDING.enquiryMessageInput}
                                    placeholder="Tell me about what you’re building, dates, audience, or context…"
                                    className="w-full bg-transparent border border-ink p-4 font-sans text-base text-ink placeholder:text-ink-muted/60 focus:outline-none focus:border-rust transition-colors disabled:opacity-60 resize-none"
                                />
                            </div>
                            <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted/70">
                                    Personal reply within 5 business days.
                                </p>
                                <button
                                    type="submit"
                                    disabled={loading || submitted}
                                    data-testid={LANDING.enquirySubmitBtn}
                                    className="bg-ink text-ivory font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 hover:bg-rust transition-colors disabled:opacity-60"
                                >
                                    {submitted
                                        ? "Sent"
                                        : loading
                                        ? "…"
                                        : "Send enquiry →"}
                                </button>
                            </div>
                            {submitted && (
                                <p
                                    data-testid={LANDING.enquirySuccess}
                                    className="md:col-span-2 font-mono text-[11px] uppercase tracking-[0.2em] text-rust"
                                >
                                    Received. You’ll hear back from Naomi directly.
                                </p>
                            )}
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
}

// ============ Footer ============

function Footer({ scrollToPricing, scrollToAdvisory, scrollToEnquiries }) {
    return (
        <footer className="bg-ink text-ivory pt-20 pb-10">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-10">
                    {/* Tagline column */}
                    <div className="md:col-span-5">
                        <p className="font-display text-3xl md:text-4xl text-ivory leading-tight">
                            I built one of the first creator management companies in the
                            world.{" "}
                            <em className="italic text-ivory/70">
                                Now I help organisations and individuals build theirs.
                            </em>
                        </p>
                        <p className="mt-8 font-display italic text-xl text-ivory/60">
                            Sign slower. Understand deeper. Build bigger.
                        </p>
                    </div>

                    {/* Spacer */}
                    <div className="hidden md:block md:col-span-1" />

                    {/* Masterclass */}
                    <div className="md:col-span-2">
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ivory/50 mb-5">
                            Masterclass
                        </p>
                        <ul className="space-y-3 font-sans text-sm text-ivory/80">
                            <li>
                                <a href="#curriculum" className="hover:text-rust transition-colors">
                                    Curriculum
                                </a>
                            </li>
                            <li>
                                <a href="#pricing" className="hover:text-rust transition-colors">
                                    Pricing
                                </a>
                            </li>
                            <li>
                                <button
                                    type="button"
                                    onClick={scrollToPricing}
                                    className="hover:text-rust transition-colors text-left"
                                >
                                    Join the masterclass
                                </button>
                            </li>
                        </ul>
                    </div>

                    {/* Work with me */}
                    <div className="md:col-span-2">
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ivory/50 mb-5">
                            Work with me
                        </p>
                        <ul className="space-y-3 font-sans text-sm text-ivory/80">
                            <li>
                                <a href="#advisory" className="hover:text-rust transition-colors">
                                    Advisory
                                </a>
                            </li>
                            <li>
                                <a href="#advisory" className="hover:text-rust transition-colors">
                                    Influencer Growth
                                </a>
                            </li>
                            <li>
                                <button
                                    type="button"
                                    onClick={scrollToEnquiries}
                                    data-testid={LANDING.footerContactLink}
                                    className="hover:text-rust transition-colors text-left"
                                >
                                    Get in touch
                                </button>
                            </li>
                        </ul>
                    </div>

                    {/* Naomi */}
                    <div className="md:col-span-2">
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ivory/50 mb-5">
                            Naomi
                        </p>
                        <ul className="space-y-3 font-sans text-sm text-ivory/80">
                            <li>
                                <a href="#weekly" className="hover:text-rust transition-colors">
                                    Newsletter
                                </a>
                            </li>
                            <li>
                                <a
                                    href="https://www.linkedin.com/in/naomilennon/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:text-rust transition-colors"
                                >
                                    LinkedIn
                                </a>
                            </li>
                            <li>
                                <a
                                    href="mailto:naomi@naomilennon.com"
                                    className="hover:text-rust transition-colors"
                                >
                                    naomi@naomilennon.com
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="mt-16 pt-8 border-t border-ivory/15 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <p className="font-display text-xl text-ivory">Naomi Lennon</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ivory/50">
                        © 2026 Naomi Lennon · naomilennon.com
                    </p>
                </div>
            </div>
        </footer>
    );
}

// ============ Page ============

export default function Landing() {
    const rootRef = useReveal();

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
    };
    const scrollToPricing = () => scrollTo("pricing");
    const scrollToAdvisory = () => scrollTo("advisory");
    const scrollToEnquiries = () => scrollTo("enquiries");

    return (
        <div ref={rootRef} className="bg-ivory text-ink font-sans">
            <Nav onJoin={scrollToPricing} onWorkWithMe={scrollToAdvisory} />
            <Hero
                scrollToPricing={scrollToPricing}
                scrollToAdvisory={scrollToAdvisory}
            />
            <CameronQuote />
            <CurrentWork />
            <TwoPaths
                scrollToAdvisory={scrollToAdvisory}
                scrollToPricing={scrollToPricing}
            />
            <TheProblem />
            <TheOpportunity />
            <ThePrize />
            <ThePhilosophy />
            <WhatISee />
            <WhatTheyDo />
            <FirstCreator />
            <AISection />
            <ThreePillars />
            <WhatYoullLearn />
            <WhoThisIsFor />
            <WhoThisIsNotFor />
            <WhyNaomi />
            <AdvisoryConsulting scrollToEnquiries={scrollToEnquiries} />
            <Timeline />
            <Curriculum />
            <FoundingCohort scrollToPricing={scrollToPricing} />
            <Become />
            <FutureVision />
            <TheWeekly />
            <Guarantee />
            <Pricing />
            <Enquiries />
            <Footer
                scrollToPricing={scrollToPricing}
                scrollToAdvisory={scrollToAdvisory}
                scrollToEnquiries={scrollToEnquiries}
            />
        </div>
    );
}
